import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Search, X } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';

function today() {
  return new Date().toISOString().slice(0, 10);
}

type ScheduleLine = {
  id: number;
  fiscalYear: number;
  periodStart: string;
  periodEnd: string;
  amount: number;
  accumulated: number;
  netBookValue: number;
  posted: boolean;
  entryId?: number;
};

type Asset = {
  id: number;
  label: string;
  assetAccount: string;
  depreciationAccount: string;
  expenseAccount: string;
  acquisitionDate: string;
  inServiceDate: string;
  grossAmount: number;
  salvageValue: number;
  usefulLifeYears: number;
  method: string;
  status: string;
  acquisitionEntryId?: number;
  disposalDate?: string;
  note?: string;
  accumulated: number;
  netBookValue: number;
  schedule: ScheduleLine[];
};

type AccountOption = {
  number: string;
  label: string;
  accountClass: number;
  nature: string;
  active: boolean;
};

export default function Assets() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data } = useQuery<Asset[]>({
    queryKey: ['assets'],
    queryFn: () => api.get('/assets').then((r) => r.data),
  });
  const assets = data ?? [];

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState<Asset | null>(null);
  const [postYear, setPostYear] = useState<number>(new Date().getFullYear());
  const [banner, setBanner] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/assets/${id}`).then((r) => r.data),
    onSuccess: () => {
      setDetail(null);
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
    onError: (e: any) => window.alert(e?.response?.data?.message ?? t('assets.deleteError')),
  });

  const postYearMut = useMutation({
    mutationFn: (year: number) =>
      api.post(`/assets/depreciation/${year}`).then((r) => r.data),
    onSuccess: (res: { year: number; posted: number }) => {
      setBanner(t('assets.postYearBulkDone', { count: res.posted }));
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (e: any) => window.alert(e?.response?.data?.message ?? t('assets.postError')),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) =>
      [a.label, a.assetAccount, a.depreciationAccount]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [assets, search]);

  const totals = useMemo(
    () =>
      assets.reduce(
        (acc, a) => ({
          gross: acc.gross + Number(a.grossAmount || 0),
          accumulated: acc.accumulated + Number(a.accumulated || 0),
          net: acc.net + Number(a.netBookValue || 0),
        }),
        { gross: 0, accumulated: 0, net: 0 },
      ),
    [assets],
  );

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow">{t('nav.assets')}</p>
          <h1 className="page-title mt-1">{t('assets.title')}</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-muted">{t('assets.intro')}</p>
        </div>
        <button type="button" onClick={() => setShowForm(true)} className="btn btn-primary">
          <Plus size={16} /> {t('assets.new')}
        </button>
      </header>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label={t('assets.totalGross')} value={euro(totals.gross)} tone="ink" />
        <KpiCard label={t('assets.totalAccumulated')} value={euro(totals.accumulated)} tone="expense" />
        <KpiCard label={t('assets.totalNet')} value={euro(totals.net)} tone="brand" />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('assets.search')}
            className="input w-full pl-9"
          />
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div>
            <label className="field-label">{t('assets.scheduleYear')}</label>
            <input
              type="number"
              value={postYear}
              onChange={(e) => setPostYear(Number(e.target.value))}
              className="input tnum w-28"
            />
          </div>
          <button
            type="button"
            onClick={() => postYearMut.mutate(postYear)}
            disabled={postYearMut.isPending}
            className="btn btn-ghost"
          >
            {t('assets.postYearBulk', { year: postYear })}
          </button>
        </div>
      </div>

      {banner && (
        <div className="mb-4 rounded-lg border border-brand/30 bg-brand-tint px-4 py-2.5 text-sm font-medium text-brand-strong">
          {banner}
        </div>
      )}

      <div className="card p-3">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('assets.label')}</th>
                <th>{t('assets.assetAccount')}</th>
                <th>{t('assets.acquisitionDate')}</th>
                <th className="num">{t('assets.gross')}</th>
                <th className="num">{t('assets.accumulated')}</th>
                <th className="num">{t('assets.net')}</th>
                <th>{t('assets.status')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => setDetail(a)}
                  className="cursor-pointer hover:bg-surface-raised"
                >
                  <td data-label={t('assets.label')} className="font-medium">{a.label}</td>
                  <td data-label={t('assets.assetAccount')} className="tnum text-ink-soft">{a.assetAccount}</td>
                  <td data-label={t('assets.acquisitionDate')} className="whitespace-nowrap text-ink-soft">{dateFmt(a.acquisitionDate)}</td>
                  <td data-label={t('assets.gross')} className="num">{euro(a.grossAmount)}</td>
                  <td data-label={t('assets.accumulated')} className="num text-expense">{euro(a.accumulated)}</td>
                  <td data-label={t('assets.net')} className="num font-medium">{euro(a.netBookValue)}</td>
                  <td data-label={t('assets.status')}>
                    <StatusPill status={a.status} />
                  </td>
                </tr>
              ))}
              {assets.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-ink-muted">
                    {t('assets.empty')}
                  </td>
                </tr>
              )}
              {assets.length > 0 && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-ink-muted">
                    {t('assets.noResults')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <AssetForm onClose={() => setShowForm(false)} />}
      {detail && (
        <AssetDetail
          assetId={detail.id}
          onClose={() => setDetail(null)}
          onDelete={() => {
            if (window.confirm(t('assets.confirmDelete'))) remove.mutate(detail.id);
          }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, tone }: { label: string; value: string; tone: 'ink' | 'brand' | 'expense' }) {
  const color = tone === 'brand' ? 'text-brand-strong' : tone === 'expense' ? 'text-expense' : 'text-ink';
  return (
    <div className="card p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-ink-muted">{label}</div>
      <div className={`mt-1 text-xl font-semibold tnum ${color}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const cls =
    status === 'IN_SERVICE'
      ? 'pill-posted'
      : status === 'FULLY_DEPRECIATED'
        ? 'pill-neutral'
        : 'pill-draft';
  return (
    <span className={`pill ${cls}`}>
      {t(`assets.statusValue.${status}`, { defaultValue: status })}
    </span>
  );
}

function AssetForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: accountsData } = useQuery<AccountOption[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get('/accounts').then((r) => r.data),
  });
  const accounts = accountsData ?? [];

  const assetAccounts = useMemo(
    () =>
      accounts.filter(
        (a) =>
          a.active &&
          a.nature === 'DETAIL' &&
          a.number.startsWith('2') &&
          !a.number.startsWith('28') &&
          !a.number.startsWith('29'),
      ),
    [accounts],
  );
  const depreciationAccounts = useMemo(
    () => accounts.filter((a) => a.active && a.number.startsWith('28')),
    [accounts],
  );

  const [label, setLabel] = useState('');
  const [assetAccount, setAssetAccount] = useState('');
  const [depreciationAccount, setDepreciationAccount] = useState('');
  const [acquisitionDate, setAcquisitionDate] = useState(today());
  const [inServiceDate, setInServiceDate] = useState(today());
  const [grossAmount, setGrossAmount] = useState('');
  const [salvageValue, setSalvageValue] = useState('');
  const [usefulLifeYears, setUsefulLifeYears] = useState('5');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api
        .post('/assets', {
          label: label.trim(),
          assetAccount,
          depreciationAccount,
          acquisitionDate,
          inServiceDate,
          grossAmount: Number(grossAmount),
          salvageValue: salvageValue ? Number(salvageValue) : 0,
          usefulLifeYears: Number(usefulLifeYears),
          method: 'LINEAR',
          note: note.trim() || null,
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('assets.saveError')),
  });

  const submit = () => {
    if (
      !label.trim() ||
      !assetAccount ||
      !depreciationAccount ||
      !acquisitionDate ||
      !grossAmount ||
      !usefulLifeYears
    ) {
      setError(t('assets.requiredError'));
      return;
    }
    setError(null);
    create.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="card max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="page-title text-lg">{t('assets.formTitle')}</h2>
          <button onClick={onClose} className="btn btn-ghost !px-2 !py-1" aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="field-label">{t('assets.label')}</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} className="input w-full" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label">{t('assets.assetAccount')}</label>
              <select
                value={assetAccount}
                onChange={(e) => setAssetAccount(e.target.value)}
                className="input w-full"
              >
                <option value="">{t('assets.selectAccount')}</option>
                {assetAccounts.map((a) => (
                  <option key={a.number} value={a.number}>
                    {a.number} - {a.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">{t('assets.depreciationAccount')}</label>
              <select
                value={depreciationAccount}
                onChange={(e) => setDepreciationAccount(e.target.value)}
                className="input w-full"
              >
                <option value="">{t('assets.selectAccount')}</option>
                {depreciationAccounts.map((a) => (
                  <option key={a.number} value={a.number}>
                    {a.number} - {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label">{t('assets.acquisitionDate')}</label>
              <input
                type="date"
                value={acquisitionDate}
                onChange={(e) => {
                  setAcquisitionDate(e.target.value);
                  if (!inServiceDate) setInServiceDate(e.target.value);
                }}
                className="input w-full"
              />
            </div>
            <div>
              <label className="field-label">{t('assets.inServiceDate')}</label>
              <input
                type="date"
                value={inServiceDate}
                onChange={(e) => setInServiceDate(e.target.value)}
                className="input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="field-label">{t('assets.grossAmount')}</label>
              <input
                type="number"
                step="0.01"
                value={grossAmount}
                onChange={(e) => setGrossAmount(e.target.value)}
                className="input tnum w-full"
              />
            </div>
            <div>
              <label className="field-label">{t('assets.salvageValue')}</label>
              <input
                type="number"
                step="0.01"
                value={salvageValue}
                onChange={(e) => setSalvageValue(e.target.value)}
                className="input tnum w-full"
              />
            </div>
            <div>
              <label className="field-label">{t('assets.usefulLifeYears')}</label>
              <input
                type="number"
                min="1"
                value={usefulLifeYears}
                onChange={(e) => setUsefulLifeYears(e.target.value)}
                className="input tnum w-full"
              />
            </div>
          </div>

          <div>
            <label className="field-label">{t('assets.note')}</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="input w-full" />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          {error && <span className="mr-auto text-sm font-medium text-expense">{error}</span>}
          <button onClick={onClose} className="btn btn-ghost">
            {t('common.cancel')}
          </button>
          <button onClick={submit} disabled={create.isPending} className="btn btn-primary">
            {t('assets.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetDetail({
  assetId,
  onClose,
  onDelete,
}: {
  assetId: number;
  onClose: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: asset } = useQuery<Asset>({
    queryKey: ['asset', assetId],
    queryFn: () => api.get(`/assets/${assetId}`).then((r) => r.data),
  });

  const post = useMutation({
    mutationFn: (year: number) =>
      api.post(`/assets/${assetId}/depreciation/${year}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asset', assetId] });
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (e: any) => window.alert(e?.response?.data?.message ?? t('assets.postError')),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="page-title text-lg">{asset?.label ?? '…'}</h2>
            {asset && (
              <p className="mt-1 text-sm text-ink-muted">
                {asset.assetAccount} · {t('assets.detailTitle')}
              </p>
            )}
          </div>
          <button onClick={onClose} className="btn btn-ghost !px-2 !py-1" aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>

        {asset && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Summary label={t('assets.gross')} value={euro(asset.grossAmount)} />
              <Summary label={t('assets.accumulated')} value={euro(asset.accumulated)} />
              <Summary label={t('assets.net')} value={euro(asset.netBookValue)} />
              <Summary
                label={t('assets.usefulLifeYears')}
                value={String(asset.usefulLifeYears)}
              />
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('assets.scheduleYear')}</th>
                    <th className="num">{t('assets.scheduleAmount')}</th>
                    <th className="num">{t('assets.scheduleAccumulated')}</th>
                    <th className="num">{t('assets.scheduleNet')}</th>
                    <th className="text-right">{t('field.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {asset.schedule.map((l) => (
                    <tr key={l.id}>
                      <td data-label={t('assets.scheduleYear')} className="tnum font-medium">{l.fiscalYear}</td>
                      <td data-label={t('assets.scheduleAmount')} className="num">{euro(l.amount)}</td>
                      <td data-label={t('assets.scheduleAccumulated')} className="num text-ink-soft">{euro(l.accumulated)}</td>
                      <td data-label={t('assets.scheduleNet')} className="num">{euro(l.netBookValue)}</td>
                      <td className="cell-actions text-right">
                        {l.posted ? (
                          <span className="pill pill-neutral">{t('assets.posted')}</span>
                        ) : (
                          <button
                            onClick={() => post.mutate(l.fiscalYear)}
                            disabled={post.isPending}
                            className="btn btn-ghost !px-2 !py-1 text-brand-strong"
                          >
                            {t('assets.postYear')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {asset.schedule.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-ink-muted">
                        {t('assets.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onDelete} className="btn btn-ghost text-expense">
            {t('common.delete')}
          </button>
          <button onClick={onClose} className="btn btn-primary">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-surface-raised p-3">
      <div className="text-xs text-ink-muted">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tnum text-ink">{value}</div>
    </div>
  );
}
